import { type InputHTMLAttributes, useEffect, useState } from "react";
import { cx } from "../cx";
import styles from "./DurationInput.module.css";
import { type DurationFormat, formatDuration, parseDuration } from "./duration";

export interface DurationInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "defaultValue" | "type"> {
  /** Seconds. */
  value: number | null;
  onChange: (seconds: number) => void;
  format?: DurationFormat;
  /** Borderless until hover/focus, like the tracker row duration. */
  inline?: boolean;
}

/** Free-text duration box: parses on blur/Enter, reverts on invalid input or Escape. */
export function DurationInput({
  value,
  onChange,
  format = "full",
  inline,
  className,
  onBlur,
  onKeyDown,
  ...rest
}: DurationInputProps) {
  const formatted = value === null ? "" : formatDuration(value, format);
  const [text, setText] = useState(formatted);
  useEffect(() => setText(formatted), [formatted]);

  const commit = () => {
    const parsed = parseDuration(text);
    if (parsed === null) {
      setText(formatted);
      return;
    }
    setText(formatDuration(parsed, format));
    if (parsed !== value) onChange(parsed);
  };

  return (
    <input
      type="text"
      autoComplete="off"
      className={cx(styles.input, inline && styles.inline, className)}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") setText(formatted);
        onKeyDown?.(e);
      }}
      {...rest}
    />
  );
}
